import { useEffect, useState } from 'react';
import { cantineApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ForkKnife, CalendarBlank, Trash } from '@phosphor-icons/react';

const todayISO = () => new Date().toISOString().slice(0, 10);

const CantineMenus = () => {
  const { t } = useLanguage();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: todayISO(), starter: '', main: '', dessert: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const start = todayISO();
      const end = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
      const { data } = await cantineApi.listMenus({ start, end });
      setMenus(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.date) { toast.error(t('error')); return; }
    setSaving(true);
    try {
      await cantineApi.upsertMenu(form);
      toast.success(t('menuSaved'));
      setForm({ date: todayISO(), starter: '', main: '', dessert: '', notes: '' });
      load();
    } catch { toast.error(t('error')); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t('deleteMenu'))) return;
    try { await cantineApi.deleteMenu(id); toast.success(t('success')); load(); }
    catch { toast.error(t('error')); }
  };

  return (
    <div className="space-y-6" data-testid="cantine-menus">
      <div className="flex items-center gap-3">
        <ForkKnife size={28} className="text-[#0052FF]" weight="duotone" />
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('cantineMenus')}</h1>
      </div>

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader><CardTitle className="font-outfit text-base">{t('newMenu')}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>{t('date')}</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="menu-date" /></div>
            <div><Label>{t('starter')}</Label><Input value={form.starter} onChange={e => setForm({ ...form, starter: e.target.value })} data-testid="menu-starter" /></div>
            <div><Label>{t('mainCourse')}</Label><Input value={form.main} onChange={e => setForm({ ...form, main: e.target.value })} data-testid="menu-main" /></div>
            <div><Label>{t('dessert')}</Label><Input value={form.dessert} onChange={e => setForm({ ...form, dessert: e.target.value })} data-testid="menu-dessert" /></div>
            <div className="md:col-span-2"><Label>{t('notes')}</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="menu-notes" /></div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="menu-save-btn">{saving ? t('loading') : t('save')}</Button>
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader><CardTitle className="font-outfit text-base">{t('upcomingMenus')}</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-[#64748B]">{t('loading')}</p>
            : menus.length === 0 ? <p className="text-sm text-[#64748B]">{t('noScheduledMenu')}</p>
            : (
              <ul className="divide-y divide-[#F1F5F9]">
                {menus.map(m => (
                  <li key={m.id} className="py-3 flex items-start justify-between gap-3" data-testid={`menu-item-${m.date}`}>
                    <div className="flex items-start gap-3 flex-1">
                      <CalendarBlank size={18} className="text-[#0052FF] mt-1" />
                      <div>
                        <p className="font-semibold text-sm">{m.date}</p>
                        <p className="text-xs text-[#64748B]">
                          {[m.starter, m.main, m.dessert].filter(Boolean).join(' · ') || '—'}
                        </p>
                        {m.notes && <p className="text-xs text-[#94A3B8] italic">{m.notes}</p>}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => remove(m.id)} data-testid={`menu-delete-${m.date}`}>
                      <Trash size={16} className="text-red-500" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CantineMenus;
